import { useState } from "react";
import { VEHICLE_INSPECTION_ITEM_LABELS } from "@carry/contract";
import { Button, cn, Textarea } from "@carry/ui";
import { BottomBar } from "../components/BottomBar";
import { Header } from "../components/Header";
import { ReportCard, ReportSectionTitle } from "../components/ReportCard";
import {
  createVehicleInspection,
  useDriverVehicles,
  type CreateVehicleInspectionInput,
  type DriverFetcher,
  type DriverVehicleOption,
  type InspectionItemResult,
} from "../hooks";
import { enqueueAction } from "../offline-queue";

const INSPECTION_LABELS = Object.values(VEHICLE_INSPECTION_ITEM_LABELS);

function formatVehicleLabel(vehicle: DriverVehicleOption): string {
  const number = vehicle.vehicle_number.trim();
  const name = vehicle.vehicle_name?.trim() ?? "";
  if (number && name) return `${number} (${name})`;
  if (number) return number;
  if (name) return name;
  return "車両名未設定";
}

function uniqueVehicleIds(ids: readonly string[]): string[] {
  return Array.from(new Set(ids.map((id) => id.trim()).filter(Boolean)));
}

/**
 * 点検対象車両の解決 (#401 決定 2)。
 * 対象は当日確定ルートの割当車両のみ。所有車 (user_driver_id) や全車両へのフォールバックは
 * 「乗車車両 = 点検対象」の原則に反するため撤去した。出勤には確定ルートが必要 (#338) で、
 * 配車確定には車両割当が必須 (#399 決定) のため、出勤済みなら常に解決できる想定。
 * 解決できないのは配車データの異常時のみで、その場合は点検入力に進ませない。
 */
function resolveRouteVehicle(
  assignedVehiclePublicIds: readonly string[],
  vehicles: DriverVehicleOption[] | null,
): { id: string; label: string } | null {
  const id = uniqueVehicleIds(assignedVehiclePublicIds)[0];
  if (!id) return null;
  const vehicle = (vehicles ?? []).find((v) => v.id === id);
  return { id, label: vehicle ? formatVehicleLabel(vehicle) : "割当車両" };
}

export function InspectionScreen({
  fetcher,
  assignedVehiclePublicIds = [],
  onBack,
  onDone,
}: {
  fetcher: DriverFetcher;
  assignedVehiclePublicIds?: readonly string[];
  onBack: () => void;
  /** 点検の記録が完了したときに呼ばれる (日報画面のバッジ更新用) */
  onDone?: () => void;
}) {
  const vehicles = useDriverVehicles(fetcher);
  // 車両選択は廃止。当日ルートの割当車両のみを自動確定する (#401 決定 2)。
  const routeVehicle = resolveRouteVehicle(assignedVehiclePublicIds, vehicles.vehicles);
  const vehicle = routeVehicle?.id ?? "";
  const selectedVehicleLabel = routeVehicle?.label ?? "車両未割当";
  const [items, setItems] = useState<Record<string, InspectionItemResult>>(() =>
    Object.fromEntries(INSPECTION_LABELS.map((_, i) => [`item${i + 1}`, "ok"])),
  );
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [ngConfirmOpen, setNgConfirmOpen] = useState(false);

  // NG 項目があるかどうか (レンダー時の派生値)。安全部品 NG のまま完了させないための注意喚起に使う。
  const ngKeys = INSPECTION_LABELS.map((_, i) => `item${i + 1}`).filter(
    (key) => (items[key] ?? "ok") === "ng",
  );
  const hasNg = ngKeys.length > 0;
  // NG があるのに備考が空 → 状況メモが残らないため送信を抑止し、詳細入力を促す。
  const ngNeedsNotes = hasNg && notes.trim() === "";

  const submitInspection = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const input: CreateVehicleInspectionInput = {
        vehicle_public_id: vehicle,
        item1: items.item1 ?? "ok",
        item2: items.item2 ?? "ok",
        item3: items.item3 ?? "ok",
        item4: items.item4 ?? "ok",
        item5: items.item5 ?? "ok",
        item6: items.item6 ?? "ok",
        item7: items.item7 ?? "ok",
        item8: items.item8 ?? "ok",
        item9: items.item9 ?? "ok",
        item10: items.item10 ?? "ok",
        item11: items.item11 ?? "ok",
        item12: items.item12 ?? "ok",
        notes: notes || undefined,
      };
      try {
        await createVehicleInspection(fetcher, input);
      } catch (e) {
        enqueueAction({
          kind: "create-vehicle-inspection",
          path: "/api/v1/driver/vehicle-inspections",
          method: "POST",
          body: input,
        });
        throw e;
      }
      setSubmitted(true);
      onDone?.();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!vehicle) {
      setSubmitError("車両を選択してください");
      return;
    }
    if (ngNeedsNotes) {
      setSubmitError("NG 項目があります。備考に状況・整備依頼内容を入力してください");
      return;
    }
    if (hasNg) {
      setNgConfirmOpen(true);
      return;
    }
    await submitInspection();
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header title="車両点検 (12項目)" sub="始業時に実施" showBack onBack={onBack} />
      <div className="px-3 py-3.5">
        <ReportSectionTitle label="対象車両" />
        <ReportCard>
          {vehicles.loading && (
            <div className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">読込中…</div>
          )}
          {vehicles.error && (
            <div className="text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
              ⚠ {vehicles.error}
            </div>
          )}
          {!vehicles.loading && vehicle && (
            <div className="mb-2 flex items-center gap-2 text-[length:var(--fs-xs)]">
              <span className="rounded-full bg-[var(--bg-sunken)] px-2 py-1 font-semibold text-[var(--ink-2)]">
                ルート割当車両
              </span>
            </div>
          )}
          {vehicle && (
            <div className="text-[length:var(--fs-md)] font-medium text-[var(--ink-1)]">
              {selectedVehicleLabel}
            </div>
          )}
          {!vehicles.loading && !vehicle && (
            <div role="alert" className="text-[length:var(--fs-sm)] text-[var(--st-cancel-ink)]">
              本日の割当車両が確認できません。配車が確定していないか、車両が割り当てられていません。管理者に連絡してください。
            </div>
          )}
        </ReportCard>

        {/* #401: 割当車両が解決できない場合は点検入力に進ませない (エラー表示のみ) */}
        {vehicle && (
          <>
            <ReportSectionTitle
              label="点検項目"
              hint={`${INSPECTION_LABELS.length}項目`}
              className="mt-3.5"
            />
            <div className="mb-2 flex justify-end px-1">
              <button
                type="button"
                onClick={() =>
                  setItems(
                    Object.fromEntries(
                      INSPECTION_LABELS.map((_, i) => [
                        `item${i + 1}`,
                        "ok" as InspectionItemResult,
                      ]),
                    ),
                  )
                }
                className="rounded-full border border-[var(--ok)] bg-[var(--st-done-bg)] px-3 py-1 text-[length:var(--fs-sm)] font-semibold text-[var(--st-done-ink)]"
              >
                全て OK
              </button>
            </div>
            <ReportCard pad={10}>
              {INSPECTION_LABELS.map((label, i) => {
                const key = `item${i + 1}`;
                const val = items[key] ?? "ok";
                const isNg = val === "ng";
                return (
                  <div
                    key={key}
                    className={cn(
                      "flex items-center gap-1.5 py-2",
                      i < INSPECTION_LABELS.length - 1
                        ? "border-b border-[var(--rule-softer)]"
                        : "",
                    )}
                  >
                    <div
                      className={cn(
                        "flex flex-1 items-center gap-1.5 text-[length:var(--fs-sm)]",
                        isNg && "font-semibold text-[var(--danger)]",
                      )}
                    >
                      {label}
                      {isNg && (
                        <span className="rounded-full bg-[var(--st-cancel-bg)] px-1.5 py-0.5 text-[length:var(--fs-xs)] font-semibold text-[var(--st-cancel-ink)]">
                          要点検
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {(["ok", "ng"] as InspectionItemResult[]).map((r) => {
                        const selectedCls =
                          r === "ng"
                            ? "border-[1.5px] border-[var(--danger)] bg-[var(--st-cancel-bg)] text-[var(--danger)]"
                            : "border-[1.5px] border-[var(--ok)] bg-[var(--st-done-bg)] text-[var(--st-done-ink)]";
                        return (
                          <button
                            key={r}
                            type="button"
                            onClick={() => setItems((prev) => ({ ...prev, [key]: r }))}
                            className={cn(
                              "cursor-pointer rounded-full px-2.5 py-1 text-[length:var(--fs-sm)] font-semibold",
                              val === r
                                ? selectedCls
                                : "border border-[var(--rule-soft)] bg-[var(--bg-raised)] text-[var(--ink-3)]",
                            )}
                          >
                            {r.toUpperCase()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </ReportCard>

            {hasNg && (
              <div className="mt-3.5 rounded-lg border border-[var(--danger)] bg-[var(--st-cancel-bg)] px-3 py-2.5 text-[length:var(--fs-sm)] text-[var(--st-cancel-ink)]">
                <div className="font-semibold">⚠ NG 項目があります ({ngKeys.length}件)</div>
                <div className="mt-1 text-[length:var(--fs-xs)]">
                  安全な運行に支障がないか確認してください。備考に状況・整備依頼内容を記入し、
                  不安があれば運行前に管理者へ連絡してください。
                </div>
              </div>
            )}

            <ReportSectionTitle label="備考" hint={hasNg ? "必須" : "任意"} className="mt-3.5" />
            <ReportCard>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={
                  hasNg
                    ? "NG 項目の状況・整備依頼内容を入力してください"
                    : "特記事項があれば入力してください"
                }
                className="min-h-[60px] resize-y border-0 bg-transparent p-0 text-base text-[var(--ink-2)] shadow-none focus-visible:ring-0"
              />
            </ReportCard>

            {submitError && (
              <div className="mt-2.5 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
                ⚠ {submitError}
              </div>
            )}
            {submitted && (
              <div className="mt-2.5 rounded-lg bg-[var(--st-done-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-done-ink)]">
                ✓ 点検結果を記録しました
              </div>
            )}
          </>
        )}
      </div>
      {vehicle && (
        <BottomBar>
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="h-auto min-h-[var(--row-h)] px-3.5 py-3 text-[length:var(--fs-sm)] text-[var(--ink-3)]"
          >
            戻る
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting || submitted || ngNeedsNotes}
            className={cn(
              "h-auto min-h-[var(--row-h)] flex-1 px-4 py-3 text-[length:var(--fs-md)] font-semibold text-white",
              hasNg
                ? "bg-[var(--danger)] hover:bg-[var(--danger)]/90"
                : "bg-[var(--ok)] hover:bg-[var(--ok)]/90",
              submitting && "cursor-wait opacity-70",
            )}
          >
            {submitted
              ? "送信済"
              : submitting
                ? "送信中…"
                : ngNeedsNotes
                  ? "備考を入力してください"
                  : hasNg
                    ? "NG ありで記録"
                    : "点検を記録"}
          </Button>
        </BottomBar>
      )}

      {ngConfirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="inspection-ng-confirm-title"
          aria-describedby="inspection-ng-confirm-description"
          className="safe-bottom fixed inset-0 z-50 flex items-end bg-black/40 px-3"
        >
          <div className="w-full rounded-lg bg-[var(--bg-raised)] p-4 shadow-lg">
            <h2
              id="inspection-ng-confirm-title"
              className="mb-2 text-[length:var(--fs-md)] font-semibold text-[var(--ink-1)]"
            >
              NG 項目があります
            </h2>
            <p
              id="inspection-ng-confirm-description"
              className="mb-4 text-[length:var(--fs-sm)] text-[var(--ink-2)]"
            >
              この車両で運行しますか？
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" onClick={() => setNgConfirmOpen(false)}>
                戻る
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setNgConfirmOpen(false);
                  void submitInspection();
                }}
                className="bg-[var(--danger)] text-white hover:bg-[var(--danger)]/90"
              >
                運行する
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
